// Slot Asset Generator Type Definitions
// Used by the standalone "Slot Asset Generator" floating panel

import type { ImageModelId, ImageStylePreset, ImageQualityPreset } from './image-generation.types';

// === Enums ===

/** 10 component groups matching the wireframe left-panel accordion */
export enum SlotComponentGroup {
  Scene = 'scene',
  Decoration = 'decoration',
  Loading = 'loading',
  Slot = 'slot',
  Symbols = 'symbols',
  TopUI = 'top-ui',
  BottomUI = 'bottom-ui',
  Win = 'win',
  Popups = 'popups',
  Typography = 'typography',
}

/** Symbol subgroups for nested accordion under SYMBOLS */
export enum SymbolSubgroup {
  High = 'high',
  Low = 'low',
  Special = 'special',
  Golden = 'golden',
}

/** Bottom UI subgroups for nested accordion */
export enum BottomUISubgroup {
  Main = 'main',
  Second = 'second',
  Info = 'info',
}

/** Popup subgroups for nested accordion */
export enum PopupSubgroup {
  BetOption = 'bet-option',
  SpinOption = 'spin-option',
  SymbolInfo = 'symbol-info',
  BuyFeature = 'buy-feature',
  History = 'history',
  Error = 'error',
}

/** Asset output format -- determines how the AI generates and how the file is saved */
export enum AssetFormat {
  PngTransparent = 'png_transparent',  // PNG with alpha channel (symbols, UI elements)
  Png = 'png',                         // PNG opaque
  Jpg = 'jpg',                         // JPEG (backgrounds, loading screens)
  PngKeyframe = 'png_keyframe',        // Static key frame for later video/Spine conversion
}

// === Component Definition ===

/** One card in the left panel component library */
export interface SlotComponentDef {
  id: string;                           // e.g. "h-1", "spin-button-default", "popup-bg"
  name: string;                         // Editable display name
  group: SlotComponentGroup;            // Primary group for accordion placement
  subgroup?: string;                    // e.g. "high", "low", "special", "golden", "main", "second"
  width: number | null;                 // null = unspecified (let AI decide)
  height: number | null;
  format: AssetFormat;                  // Output format
  assetPath: string;                    // Original asset path from pixi-slot
  notes: string;                        // Editable style hints (passed into prompt as context)
  referenceImages: string[];            // Blob URLs or relative paths for ref thumbnails
  deriveFrom?: string;                  // For golden variants -- base symbol ID (e.g. "h-1")
  symbolId?: number;                    // Symbol numeric ID (1-69) if applicable
  isCustom?: boolean;                   // true for user-added components (not in default registry)
  prompt?: string;                      // Persisted prompt text for this component
}

// === Generation Request ===

/** Sent to the generation service when user clicks "Generate" */
export interface SlotAssetGenerationRequest {
  component: SlotComponentDef;
  prompt: string;
  sessionReferenceImages: string[];     // Extra refs added in right panel (session-only)
  imageCount: 1 | 2 | 4 | 8;
  style: ImageStylePreset;
  quality: ImageQualityPreset;
  model: ImageModelId;
  systemContextOverride?: string;
}

// === Results ===

/** Single result image from generation */
export interface SlotAssetResult {
  id: string;
  imageUrl: string;                     // Blob URL
  status: 'ready' | 'generating' | 'saved' | 'error';
  savedPath?: string;                   // Relative path from project root after "Use This"
  error?: string;
}

/** Generation session state (right panel + results modal) */
export interface SlotAssetGenerationState {
  status: 'idle' | 'generating' | 'results' | 'error';
  request: SlotAssetGenerationRequest | null;
  results: SlotAssetResult[];
  progress: { completed: number; total: number };
  error: string | null;
  elapsedMs: number;
}

// === UI State ===

/** Group metadata for the accordion UI */
export interface SlotComponentGroupMeta {
  id: SlotComponentGroup;
  label: string;
  collapsed: boolean;
}

/** Toast notification for saved images */
export interface SavedToast {
  id: string;
  savedPath: string;
  componentName: string;
  timestamp: number;
}

// === Store Shape ===

export interface SlotAssetGeneratorState {
  // Component registry (mutable -- user can edit cards)
  components: SlotComponentDef[];

  // Selection
  selectedComponentId: string | null;

  // Generation
  generation: SlotAssetGenerationState;

  // UI state
  expandedGroups: Set<string>;
  leftPanelLocked: boolean;
  leftPanelWidth: number;

  // Results modal
  resultsModalOpen: boolean;
  lightboxIndex: number | null;        // null = closed, 0-N = which image

  // Toasts
  toasts: SavedToast[];

  // History
  history: SlotGenerationHistoryEntry[];
  historyPanelOpen: boolean;
}

export interface SlotAssetGeneratorActions {
  // Selection
  selectComponent: (id: string) => void;

  // Component CRUD
  updateComponent: (id: string, patch: Partial<SlotComponentDef>) => void;
  resetComponentToDefault: (id: string) => void;
  addCustomComponent: (def: Partial<SlotComponentDef>) => void;
  removeCustomComponent: (id: string) => void;

  // Groups
  toggleGroup: (group: string) => void;
  setLeftPanelWidth: (width: number) => void;

  // Generation lifecycle
  startGeneration: (request: SlotAssetGenerationRequest) => void;
  updateGenerationProgress: (completed: number, total: number) => void;
  setGenerationResult: (index: number, result: SlotAssetResult) => void;
  setGenerationError: (error: string) => void;
  cancelGeneration: () => void;
  resetGeneration: () => void;

  // Results modal
  openResultsModal: () => void;
  closeResultsModal: () => void;
  openLightbox: (index: number) => void;
  closeLightbox: () => void;

  // Save
  markImageSaved: (resultId: string, savedPath: string) => void;
  startReroll: (resultId: string) => void;

  // Toasts
  addToast: (toast: SavedToast) => void;
  removeToast: (id: string) => void;

  // History
  addHistoryEntry: (entry: SlotGenerationHistoryEntry) => void;
  removeHistoryEntry: (id: string) => void;
  clearHistory: () => void;
  toggleHistoryPanel: () => void;
}

// === History ===

/** One recorded generation session */
export interface SlotGenerationHistoryEntry {
  id: string;
  componentId: string;
  componentName: string;
  prompt: string;
  model: string;
  style: string;
  quality: string;
  imageCount: number;
  /** Relative paths of saved result images (persists across sessions) */
  savedPaths: string[];
  timestamp: number;
}

// === Defaults ===

export const DEFAULT_GENERATION_STATE: SlotAssetGenerationState = {
  status: 'idle',
  request: null,
  results: [],
  progress: { completed: 0, total: 0 },
  error: null,
  elapsedMs: 0,
};

/** Golden variant prompt suffix (auto-appended when generating golden symbols) */
export const GOLDEN_PROMPT_SUFFIX =
  'golden version, covered in shimmering gold, glowing aura, metallic luxury finish, same composition as the base symbol';
